// Post history analytics service

export interface TweetData {
  id: string;
  text: string;
  timestamp: string; // ISO 8601
  likes?: number;
  retweets?: number;
  replies?: number;
  views?: number;
  impressions?: number;
}

export interface TopicCluster {
  id: string;
  name: string;
  keywords: string[];
  tweets: string[]; // tweet IDs
  avgEngagement: number;
  color: string;
}

export interface TimeSlotData {
  hour: number;
  dayOfWeek: number; // 0=Sunday, 6=Saturday
  count: number;
  avgEngagement: number;
}

export interface PerformanceMetrics {
  totalTweets: number;
  totalLikes: number;
  totalRetweets: number;
  totalReplies: number;
  avgLikes: number;
  avgRetweets: number;
  avgReplies: number;
  bestTweet: TweetData | null;
  worstTweet: TweetData | null;
}

export interface TrendData {
  date: string;
  tweets: number;
  likes: number;
  retweets: number;
  replies: number;
  engagement: number;
}

const STORAGE_KEY = 'bangerfy_analytics_data';

export const analyticsService = {
  saveTweets(tweets: TweetData[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tweets));
  },

  getTweets(): TweetData[] {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];
    try {
      return JSON.parse(data);
    } catch {
      return [];
    }
  },

  addTweet(tweet: TweetData): void {
    const tweets = this.getTweets();
    tweets.unshift(tweet);
    this.saveTweets(tweets);
  },

  calculateMetrics(): PerformanceMetrics {
    const tweets = this.getTweets();

    if (tweets.length === 0) {
      return {
        totalTweets: 0,
        totalLikes: 0,
        totalRetweets: 0,
        totalReplies: 0,
        avgLikes: 0,
        avgRetweets: 0,
        avgReplies: 0,
        bestTweet: null,
        worstTweet: null,
      };
    }

    const totalLikes = tweets.reduce((sum, t) => sum + (t.likes || 0), 0);
    const totalRetweets = tweets.reduce((sum, t) => sum + (t.retweets || 0), 0);
    const totalReplies = tweets.reduce((sum, t) => sum + (t.replies || 0), 0);

    const withEngagement = tweets.map(t => ({
      ...t,
      engagement: (t.likes || 0) + (t.retweets || 0) * 2 + (t.replies || 0) * 3,
    }));

    const bestTweet = withEngagement.reduce((best, t) =>
      t.engagement > best.engagement ? t : best
    );

    const worstTweet = withEngagement.reduce((worst, t) =>
      t.engagement < worst.engagement ? t : worst
    );

    return {
      totalTweets: tweets.length,
      totalLikes,
      totalRetweets,
      totalReplies,
      avgLikes: totalLikes / tweets.length,
      avgRetweets: totalRetweets / tweets.length,
      avgReplies: totalReplies / tweets.length,
      bestTweet,
      worstTweet,
    };
  },

  classifyTopics(maxClusters: number = 5): TopicCluster[] {
    const tweets = this.getTweets();
    if (tweets.length === 0) return [];

    // English stop words
    const stopWords = new Set([
      'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
      'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
      'should', 'may', 'might', 'can', 'shall', 'to', 'of', 'in', 'for',
      'on', 'with', 'at', 'by', 'from', 'as', 'into', 'through', 'during',
      'before', 'after', 'above', 'below', 'between', 'out', 'off', 'over',
      'under', 'again', 'further', 'then', 'once', 'here', 'there', 'when',
      'where', 'why', 'how', 'all', 'each', 'every', 'both', 'few', 'more',
      'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own',
      'same', 'so', 'than', 'too', 'very', 'just', 'because', 'but', 'and',
      'or', 'if', 'while', 'about', 'up', 'its', 'it', 'this', 'that',
      'these', 'those', 'i', 'me', 'my', 'we', 'our', 'you', 'your',
      'he', 'him', 'his', 'she', 'her', 'they', 'them', 'their', 'what',
      'which', 'who', 'whom', 'get', 'got', 'like', 'dont', "don't",
      'also', 'been', 'being', 'come', 'know', 'make', 'way', 'much',
      'well', 'back', 'even', 'new', 'want', 'use', 'try', 'take',
    ]);

    // Extract words (English: split by whitespace, 3+ chars)
    const extractWords = (text: string): string[] => {
      const cleaned = text
        .replace(/https?:\/\/[^\s]+/g, '')
        .replace(/[^\p{L}\p{N}\s]/gu, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      return cleaned.split(' ').filter(w => w.length >= 3 && !stopWords.has(w));
    };

    // Calculate word frequency across all tweets
    const wordFreq = new Map<string, number>();
    const tweetWords = tweets.map(t => {
      const words = extractWords(t.text);
      words.forEach(w => wordFreq.set(w, (wordFreq.get(w) || 0) + 1));
      return { tweet: t, words };
    });

    // Extract top words (appearing 3+ times)
    const topWords = Array.from(wordFreq.entries())
      .filter(([, count]) => count >= 3)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20)
      .map(([word]) => word);

    if (topWords.length === 0) {
      return [{
        id: 'other',
        name: 'Other',
        keywords: [],
        tweets: tweets.map(t => t.id),
        avgEngagement: this.calculateAvgEngagement(tweets),
        color: '#71767B',
      }];
    }

    // Use top words as cluster seeds
    const clusterSeeds = topWords.slice(0, Math.min(maxClusters, topWords.length));
    const clusters: TopicCluster[] = clusterSeeds.map((word, i) => ({
      id: `cluster-${i}`,
      name: word,
      keywords: [word],
      tweets: [],
      avgEngagement: 0,
      color: this.getClusterColor(i),
    }));

    const otherCluster: TopicCluster = {
      id: 'other',
      name: 'Other',
      keywords: [],
      tweets: [],
      avgEngagement: 0,
      color: '#71767B',
    };

    // Assign tweets to clusters
    tweetWords.forEach(({ tweet, words }) => {
      let assigned = false;
      for (const cluster of clusters) {
        if (words.some(w => cluster.keywords.includes(w))) {
          cluster.tweets.push(tweet.id);
          assigned = true;
          break;
        }
      }
      if (!assigned) {
        otherCluster.tweets.push(tweet.id);
      }
    });

    // Expand cluster keywords with co-occurring words
    clusters.forEach(cluster => {
      const clusterTweets = tweets.filter(t => cluster.tweets.includes(t.id));
      const clusterWordFreq = new Map<string, number>();

      clusterTweets.forEach(t => {
        extractWords(t.text).forEach(w => {
          if (!cluster.keywords.includes(w)) {
            clusterWordFreq.set(w, (clusterWordFreq.get(w) || 0) + 1);
          }
        });
      });

      const relatedWords = Array.from(clusterWordFreq.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([word]) => word);

      cluster.keywords.push(...relatedWords);
      cluster.avgEngagement = this.calculateAvgEngagement(clusterTweets);
    });

    otherCluster.avgEngagement = this.calculateAvgEngagement(
      tweets.filter(t => otherCluster.tweets.includes(t.id))
    );

    // Exclude empty clusters
    const validClusters = clusters.filter(c => c.tweets.length > 0);
    if (otherCluster.tweets.length > 0) {
      validClusters.push(otherCluster);
    }

    return validClusters;
  },

  analyzeTimeSlots(): TimeSlotData[] {
    const tweets = this.getTweets();
    const slots: Map<string, TimeSlotData> = new Map();

    tweets.forEach(tweet => {
      const date = new Date(tweet.timestamp);
      const hour = date.getHours();
      const dayOfWeek = date.getDay();
      const key = `${dayOfWeek}-${hour}`;

      if (!slots.has(key)) {
        slots.set(key, {
          hour,
          dayOfWeek,
          count: 0,
          avgEngagement: 0,
        });
      }

      const slot = slots.get(key)!;
      slot.count++;
      const engagement = (tweet.likes || 0) + (tweet.retweets || 0) * 2 + (tweet.replies || 0) * 3;
      slot.avgEngagement = (slot.avgEngagement * (slot.count - 1) + engagement) / slot.count;
    });

    return Array.from(slots.values());
  },

  getTrendData(days: number = 30): TrendData[] {
    const tweets = this.getTweets();
    const now = new Date();
    const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    const dailyData = new Map<string, TrendData>();

    tweets.forEach(tweet => {
      const date = new Date(tweet.timestamp);
      if (date < startDate) return;

      const dateKey = date.toISOString().split('T')[0];

      if (!dailyData.has(dateKey)) {
        dailyData.set(dateKey, {
          date: dateKey,
          tweets: 0,
          likes: 0,
          retweets: 0,
          replies: 0,
          engagement: 0,
        });
      }

      const data = dailyData.get(dateKey)!;
      data.tweets++;
      data.likes += tweet.likes || 0;
      data.retweets += tweet.retweets || 0;
      data.replies += tweet.replies || 0;
      data.engagement += (tweet.likes || 0) + (tweet.retweets || 0) * 2 + (tweet.replies || 0) * 3;
    });

    return Array.from(dailyData.values()).sort((a, b) => a.date.localeCompare(b.date));
  },

  getBestTimeSlots(limit: number = 5): TimeSlotData[] {
    const slots = this.analyzeTimeSlots();
    return slots
      .filter(s => s.count >= 2)
      .sort((a, b) => b.avgEngagement - a.avgEngagement)
      .slice(0, limit);
  },

  generateInsights(): string[] {
    const metrics = this.calculateMetrics();
    const clusters = this.classifyTopics();
    const bestSlots = this.getBestTimeSlots(3);
    const insights: string[] = [];

    if (metrics.totalTweets > 0) {
      insights.push(`Analyzed ${metrics.totalTweets} tweets total`);

      if (metrics.avgLikes > 10) {
        insights.push(`Averaging ${metrics.avgLikes.toFixed(1)} likes per post — strong performance!`);
      } else if (metrics.avgLikes > 5) {
        insights.push(`Averaging ${metrics.avgLikes.toFixed(1)} likes per post — steady growth`);
      }

      if (metrics.avgRetweets > metrics.avgLikes * 0.2) {
        insights.push(`High retweet ratio! Your content is very shareable`);
      }
    }

    if (clusters.length > 1) {
      const topCluster = clusters.reduce((best, c) =>
        c.avgEngagement > best.avgEngagement ? c : best
      );
      insights.push(`"${topCluster.name}" topics get the best engagement`);
    }

    if (bestSlots.length > 0) {
      const best = bestSlots[0];
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      insights.push(
        `${dayNames[best.dayOfWeek]} at ${best.hour}:00 is your best posting time`
      );
    }

    if (metrics.bestTweet) {
      const engagement =
        (metrics.bestTweet.likes || 0) +
        (metrics.bestTweet.retweets || 0) * 2 +
        (metrics.bestTweet.replies || 0) * 3;
      insights.push(`Your best tweet scored ${engagement} engagement`);
    }

    return insights.length > 0 ? insights : ['Not enough data yet. Add more tweets to see insights.'];
  },

  importFromCSV(csvText: string): { success: boolean; imported: number; error?: string } {
    try {
      const lines = csvText.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        return { success: false, imported: 0, error: 'Data is empty' };
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const tweets: TweetData[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',');
        const tweet: TweetData = {
          id: Date.now().toString() + Math.random().toString(36).slice(2),
          text: '',
          timestamp: new Date().toISOString(),
        };

        headers.forEach((header, idx) => {
          const value = values[idx]?.trim().replace(/^"|"$/g, '');
          if (!value) return;

          if (header.includes('text') || header.includes('tweet')) {
            tweet.text = value;
          } else if (header.includes('time') || header.includes('date')) {
            tweet.timestamp = new Date(value).toISOString();
          } else if (header.includes('like')) {
            tweet.likes = parseInt(value) || 0;
          } else if (header.includes('retweet') || header.includes('rt')) {
            tweet.retweets = parseInt(value) || 0;
          } else if (header.includes('repl')) {
            tweet.replies = parseInt(value) || 0;
          } else if (header.includes('view')) {
            tweet.views = parseInt(value) || 0;
          }
        });

        if (tweet.text) {
          tweets.push(tweet);
        }
      }

      const existing = this.getTweets();
      this.saveTweets([...tweets, ...existing]);

      return { success: true, imported: tweets.length };
    } catch (error) {
      return {
        success: false,
        imported: 0,
        error: error instanceof Error ? error.message : 'Parse error',
      };
    }
  },

  generateSampleData(count: number = 50): void {
    const topics = [
      { keywords: ['coding', 'code', 'error', 'debugging'], emojis: ['💻', '🐛', '✨'] },
      { keywords: ['coffee', 'lunch', 'cafe', 'snack'], emojis: ['☕', '🍕', '🍰'] },
      { keywords: ['learning', 'study', 'book', 'reading'], emojis: ['📚', '✏️', '📖'] },
      { keywords: ['work', 'meeting', 'deadline', 'tasks'], emojis: ['💼', '📊', '⏰'] },
    ];

    const tweets: TweetData[] = [];
    const now = Date.now();

    for (let i = 0; i < count; i++) {
      const topic = topics[Math.floor(Math.random() * topics.length)];
      const keyword = topic.keywords[Math.floor(Math.random() * topic.keywords.length)];
      const emoji = topic.emojis[Math.floor(Math.random() * topic.emojis.length)];

      const daysAgo = Math.floor(Math.random() * 30);
      const timestamp = new Date(now - daysAgo * 24 * 60 * 60 * 1000);
      timestamp.setHours(Math.floor(Math.random() * 24));

      tweets.push({
        id: `sample-${i}`,
        text: `${emoji} Another day of ${keyword}!`,
        timestamp: timestamp.toISOString(),
        likes: Math.floor(Math.random() * 50),
        retweets: Math.floor(Math.random() * 10),
        replies: Math.floor(Math.random() * 5),
        views: Math.floor(Math.random() * 500) + 100,
      });
    }

    this.saveTweets(tweets);
  },

  calculateAvgEngagement(tweets: TweetData[]): number {
    if (tweets.length === 0) return 0;
    const total = tweets.reduce((sum, t) =>
      sum + (t.likes || 0) + (t.retweets || 0) * 2 + (t.replies || 0) * 3, 0
    );
    return total / tweets.length;
  },

  getClusterColor(index: number): string {
    const colors = [
      '#1DA1F2', // Twitter Blue
      '#667eea', // Purple
      '#F59E0B', // Orange
      '#00BA7C', // Green
      '#E040FB', // Pink
      '#FF6B6B', // Red
      '#4ECDC4', // Cyan
      '#FFD93D', // Yellow
    ];
    return colors[index % colors.length];
  },
};
